import { LayoutFatalErrorOccured } from "@/components/layout-loading/layout-fatal-error-occured";
import { LayoutLoading } from "@/components/layout-loading/layout-loading";
import { useToastError } from "@/components/ui/use-toast";
import { TeamUserRolesEnum } from "@/enums/team-user-role";
import { useApi } from "@/hooks/useApi";
import { groupTeamLLMsByProvider } from "@/lib/group-team-llms";
import {
  CompanyDTO,
  Nullable,
  TeamCompanyDTO,
  TeamDTO,
  TeamLLMDTO,
  UserMeDTO,
} from "@shaarp/common";
import { usePostHog } from "posthog-js/react";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ContentManager } from "./teams-content-manager";

export type TeamLLMModelGroupDTO = {
  id: string;
  name: string;
  models: TeamLLMDTO[];
};

type TeamsContextType = {
  teams: TeamDTO[];
  isLoading: boolean;
  teamSelected: Nullable<TeamDTO>;
  teamCompanySelected: Nullable<CompanyDTO>;
  teamLLMList: TeamLLMDTO[];
  teamLLMListGrouped: TeamLLMModelGroupDTO[];
  setTeamLLMList: (teamLLMList: TeamLLMDTO[]) => void;
  apiFetchTeamLLM: () => void;
  setTeamSelected: (team: TeamDTO) => void;
  setTeamIdSelected: (teamId: string) => void;
  /** Re-reads the user's teams in the background, without a loading screen. */
  reloadMyTeams: () => Promise<void>;
  isAdmin: boolean;
  isGod: boolean;
  isManager: boolean;
  isUser: boolean;
  isBilling: boolean;
  isAdvisor: boolean;
  canViewCollaborators: boolean;
};

const TeamsContext = createContext<TeamsContextType>({
  teams: [],
  isLoading: true,
  teamSelected: null,
  teamCompanySelected: null,
  teamLLMList: [],
  teamLLMListGrouped: [],
  setTeamLLMList: () => {},
  apiFetchTeamLLM: () => {},
  setTeamSelected: () => {},
  setTeamIdSelected: () => {},
  reloadMyTeams: async () => {},
  isAdmin: false,
  isGod: false,
  isManager: false,
  isUser: true,
  isBilling: false,
  isAdvisor: false,
  canViewCollaborators: false,
});

export const LOCALSTORAGE_TEAM_SELECTED_KEY = "team-selected";

export const useTeams = (): TeamsContextType => {
  const context = useContext(TeamsContext);
  if (!context) {
    throw new Error("useTeams must be used within an TeamsProvider");
  }
  return context;
};

export const TeamsProvider = ({
  children,
}: {
  children: ReactNode;
}): ReactNode => {
  const api = useApi();
  const posthog = usePostHog();
  const toastError = useToastError();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Nullable<Error>>(null);

  const [teams, setTeams] = useState<TeamDTO[]>([]);
  const [isGod, setIsGod] = useState<boolean>(false);
  const [teamSelected, setTeamSelected] = useState<Nullable<TeamDTO>>(null);
  const [teamCompanySelected, setTeamCompanySelected] =
    useState<Nullable<CompanyDTO>>(null);

  const [teamLLMList, setTeamLLMList] = useState<TeamLLMDTO[]>([]);
  const [teamLLMListGrouped, setTeamLLMListGrouped] = useState<
    TeamLLMModelGroupDTO[]
  >([]);

  useEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    if (teamSelected) {
      fetchTeamCompany();
      fetchTeamLLM();
    }
  }, [teamSelected]);

  useEffect(() => {
    if (teamSelected && teamSelected.id) {
      posthog?.group("company", teamSelected.id, {
        name: teamSelected.name,
        status: teamSelected.status,
        user_role: teamSelected.userRole,
        user_licence: teamSelected.userLicence,
        billing_enabled: teamSelected.billingEnabled,
        billing_error: teamSelected.billingError,
        default_billing_frequency: teamSelected.defaultBillingFrequency,
        trial_ends_at: teamSelected.trialEndsAt,
        setup_completed: teamSelected.setupCompleted,
        setup_team_creation: teamSelected.setupTeamCreation,
        credits_balance: teamSelected.creditsBalance,
        monthly_discount: teamSelected.monthlyDiscount,
        annual_discount: teamSelected.annualDiscount,
        initial_pro_license_count_monthly:
          teamSelected.initialProLicenseCountMonthly,
        initial_pro_license_count_annual:
          teamSelected.initialProLicenseCountAnnual,
        stripe_monthly_subscription_status:
          teamSelected.stripeMonthlySubscriptionStatus,
        stripe_annual_subscription_status:
          teamSelected.stripeAnnualSubscriptionStatus,
      });
    }
  }, [posthog, teamSelected]);

  const setTeamIdSelected = useCallback((teamId: string): void => {
    localStorage.setItem(LOCALSTORAGE_TEAM_SELECTED_KEY, teamId);

    const newTeamSelected = teams.find((team) => team.id === teamId);
    if (teams.length > 0 && newTeamSelected) {
      setTeamSelected(newTeamSelected);
    }
  }, [teams]);

  const fetchTeams = async () => {
    try {
      setIsLoading(true);
      const [{ data }, { data: userData }] = await Promise.all([
        api.get<TeamDTO[]>(`/my/teams`),
        api.get<UserMeDTO>(`/my/user`),
      ]);
      setIsGod(userData.adminLevel === "god");
      if (data.length === 0) {
        console.warn("No teams available");
        localStorage.removeItem(LOCALSTORAGE_TEAM_SELECTED_KEY);
        return;
      }

      setTeams(data);

      let newTeamSelected;

      const localStorageTeamSelected = localStorage.getItem(
        LOCALSTORAGE_TEAM_SELECTED_KEY
      );
      if (!localStorageTeamSelected) {
        newTeamSelected = data[0];
      } else {
        const team = data.find((team) => team.id === localStorageTeamSelected);

        if (team) {
          newTeamSelected = team;
        } else {
          newTeamSelected = data[0];
        }
      }

      if (newTeamSelected) {
        setTeamSelected(newTeamSelected);
        setTeamIdSelected(newTeamSelected.id);
      }
    } catch (error) {
      toastError();
      setError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  // Picks up a change made elsewhere — a feature flag from the back office —
  // without the loading screen fetchTeams shows: the selected team keeps its
  // place and only its fields change.
  const reloadMyTeams = useCallback(async (): Promise<void> => {
    try {
      const { data } = await api.get<TeamDTO[]>(`/my/teams`);
      // Fresh rows replace their old version; a team switched into from
      // outside the member list (admin) stays in the switcher.
      setTeams((previous) => [
        ...data,
        ...previous.filter(
          (team) => !data.some((fresh) => fresh.id === team.id)
        ),
      ]);
      // The same object when nothing changed: every effect keyed on the
      // selected team would otherwise refetch for nothing.
      setTeamSelected((current) => {
        const fresh = current && data.find((team) => team.id === current.id);
        if (!fresh || JSON.stringify(fresh) === JSON.stringify(current)) {
          return current;
        }
        return fresh;
      });
    } catch {
      toastError();
    }
  }, [api, toastError]);

  const fetchTeamCompany = async () => {
    try {
      const { data } = await api.get<TeamCompanyDTO>(`/my/team/company`);
      if (data && data.company) {
        setTeamCompanySelected(data.company);
      }
    } catch (error) {
      toastError();
      setError(error as Error);
    }
  };

  const fetchTeamLLM = useCallback(async (): Promise<void> => {
    try {
      const { data } = await api.get<TeamLLMDTO[]>(`/my/team/llm`);
      setTeamLLMList(data);
      setTeamLLMListGrouped(groupTeamLLMsByProvider(data));
    } catch (error) {
      setError(error as Error);
    }
  }, [api]);

  const handleTeamSwitch = useCallback((team: TeamDTO): void => {
    setTeams((prevTeams) => {
      const teamExists = prevTeams.some((prevTeam) => prevTeam.id === team.id);
      if (teamExists) {
        return prevTeams.map((prevTeam) =>
          prevTeam.id === team.id ? team : prevTeam
        );
      } else {
        return [...prevTeams, team];
      }
    });

    localStorage.setItem(LOCALSTORAGE_TEAM_SELECTED_KEY, team.id);
    setTeamSelected(team);
  }, []);

  const isAdmin = useMemo(
    () => teams.some((team) => team.userRole === TeamUserRolesEnum.ADMIN),
    [teams],
  );
  const isManager = useMemo(
    () => teamSelected?.userRole === TeamUserRolesEnum.MANAGER ? true : false,
    [teamSelected],
  );
  const isUser = useMemo(
    () => teamSelected?.userRole === TeamUserRolesEnum.MEMBER ? true : false,
    [teamSelected],
  );
  const isBilling = useMemo(
    () => teamSelected?.userRole === TeamUserRolesEnum.BILLING ? true : false,
    [teamSelected],
  );
  const isAdvisor = useMemo(
    () => teamSelected?.userRole === TeamUserRolesEnum.ADVISOR ? true : false,
    [teamSelected],
  );
  const canViewCollaborators = useMemo(
    () => isManager || isAdvisor || isAdmin,
    [isManager, isAdvisor, isAdmin],
  );

  const value = useMemo(
    () => ({
      teams,
      isLoading,
      teamSelected,
      teamCompanySelected,
      teamLLMList,
      teamLLMListGrouped,
      setTeamLLMList,
      apiFetchTeamLLM: fetchTeamLLM,
      setTeamSelected: handleTeamSwitch,
      setTeamIdSelected,
      reloadMyTeams,
      isAdmin,
      isGod,
      isManager,
      isUser,
      isBilling,
      isAdvisor,
      canViewCollaborators,
    }),
    [
      teams,
      isLoading,
      teamSelected,
      teamCompanySelected,
      teamLLMList,
      teamLLMListGrouped,
      setTeamLLMList,
      fetchTeamLLM,
      handleTeamSwitch,
      setTeamIdSelected,
      reloadMyTeams,
      isAdmin,
      isGod,
      isManager,
      isUser,
      isBilling,
      isAdvisor,
      canViewCollaborators,
    ],
  );

  if (isLoading) {
    return <LayoutLoading />;
  }

  if (error) {
    return <LayoutFatalErrorOccured />;
  }

  return (
    <TeamsContext.Provider value={value}>
      <ContentManager
        teamSelected={teamSelected}
        isUser={isUser}
        isBilling={isBilling}
        reloadTeams={reloadMyTeams}
      >
        {children}
      </ContentManager>
    </TeamsContext.Provider>
  );
};

